import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { getFitnessMemberErrorMessage } from '../utils/fitnessMemberErrors'
import BackPillButton from '../components/BackPillButton'
import GolfCourseSplitLayout from '../components/GolfCourseSplitLayout'
import GolfDateCarousel from '../components/GolfDateCarousel'
import {
  DEFAULT_GOLF_TIME_ZONE,
  formatMonthShort,
  formatWeekdayShort,
  getTodayDateKey,
  normalizeDateKey,
  parseDateKey,
} from '../utils/golfTime'

const DEFAULT_BOOKING_WINDOW_DAYS = 14

function formatDayLabel(dateKey, timeZone, locale) {
  const weekday = formatWeekdayShort(dateKey, timeZone, locale)
  const parsed = parseDateKey(dateKey)
  const dayNum = parsed ? parsed.getUTCDate() : ''
  return `${weekday || ''} ${dayNum}`.trim()
}

function formatDuration(durationMinutes, locale) {
  if (!Number.isFinite(durationMinutes)) return null
  return new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(durationMinutes)
}

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
}

function formatPrice(value, currency, locale) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  const code = currency || ''
  const symbol = CURRENCY_SYMBOLS[code] || ''
  const formatted = numeric.toLocaleString(locale, { minimumFractionDigits: numeric % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })
  if (symbol) return `${symbol}${formatted}`
  if (code) return `${formatted} ${code}`
  return formatted
}

export default function MemberFitnessClassAvailabilityPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const { classId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet } = useMemberPortalApi()
  const [selectedDateKey, setSelectedDateKey] = useState(() => normalizeDateKey(searchParams.get('date')))
  const [fitnessClass, setFitnessClass] = useState(location.state?.fitnessClass || null)
  const [fitnessClassLoading, setFitnessClassLoading] = useState(!location.state?.fitnessClass)
  const [fitnessClassError, setFitnessClassError] = useState(null)
  const [availability, setAvailability] = useState([])
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [durationMinutes, setDurationMinutes] = useState(null)
  const [timeZone, setTimeZone] = useState(DEFAULT_GOLF_TIME_ZONE)
  const [refreshKey, setRefreshKey] = useState(0)
  const [price, setPrice] = useState(null)
  const [currency, setCurrency] = useState('GBP')
  const [bookingWindow, setBookingWindow] = useState(DEFAULT_BOOKING_WINDOW_DAYS)

  const className = fitnessClass?.name || 'Fitness class'
  const classDetails = [fitnessClass?.location, fitnessClass?.instructor].filter(Boolean).join(' • ')

  useEffect(() => {
    let cancelled = false

    async function loadFitnessClass() {
      if (!clubCode || !classId) return
      setFitnessClassLoading(true)
      setFitnessClassError(null)
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/fitness/classes/${classId}`)
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'unable_to_load_class')
        }
        if (!cancelled) {
          setFitnessClass(json?.data || null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load fitness class', err)
          setFitnessClassError(getFitnessMemberErrorMessage(err?.message, key => key))
        }
      } finally {
        if (!cancelled) {
          setFitnessClassLoading(false)
        }
      }
    }

    loadFitnessClass()

    return () => {
      cancelled = true
    }
  }, [classId, clubCode, memberPortalApiGet])

  useEffect(() => {
    if (selectedDateKey) return
    const todayKey = getTodayDateKey(DEFAULT_GOLF_TIME_ZONE)
    if (todayKey) {
      setSelectedDateKey(todayKey)
    }
  }, [selectedDateKey])

  useEffect(() => {
    if (!selectedDateKey) return
    const existingDate = searchParams.get('date')
    if (existingDate === selectedDateKey) return
    const next = new URLSearchParams(searchParams)
    next.set('date', selectedDateKey)
    navigate({ search: `?${next.toString()}` }, { replace: true })
  }, [navigate, searchParams, selectedDateKey])

  useEffect(() => {
    let cancelled = false

    async function loadAvailability() {
      if (!clubCode || !classId || !selectedDateKey) return
      setAvailabilityLoading(true)
      setAvailabilityError(null)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/fitness/classes/${classId}/availability?date=${selectedDateKey}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) {
          throw new Error(json?.error || 'availability_unavailable')
        }
        if (!cancelled) {
          setAvailability(Array.isArray(json?.slots) ? json.slots : [])
          setDurationMinutes(json?.duration_minutes ?? null)
          setTimeZone(json?.time_zone || DEFAULT_GOLF_TIME_ZONE)
          setPrice(json?.price ?? null)
          setCurrency(json?.currency || 'GBP')
          setBookingWindow(
            Number.isInteger(json?.booking_window) ? json.booking_window : DEFAULT_BOOKING_WINDOW_DAYS
          )
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load fitness availability', err)
          setAvailabilityError(getFitnessMemberErrorMessage(err?.message, key => key))
          setAvailability([])
        }
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false)
        }
      }
    }

    loadAvailability()

    return () => {
      cancelled = true
    }
  }, [classId, clubCode, memberPortalApiGet, refreshKey, selectedDateKey])

  const handleContinue = (slot) => {
    if (!slot?.start_at || !selectedDateKey) return
    const params = new URLSearchParams()
    params.set('date', selectedDateKey)
    if (slot.occurrence_id) params.set('occurrence_id', slot.occurrence_id)
    params.set('start_at', slot.start_at)
    if (slot?.start_time) params.set('start_time', slot.start_time)
    navigate(`/${clubCode}/fitness/${classId}/book?${params.toString()}`, {
      state: {
        fitnessClass,
        slot,
      },
    })
  }

  const slotMeta = useMemo(() => formatDuration(durationMinutes, locale), [durationMinutes, locale])
  const priceLabel = formatPrice(price, currency, locale)
  return (
    <GolfCourseSplitLayout heroImageUrl={fitnessClass?.photo_url || undefined} heroPosition="50% 45%">
      <div className="mp-golf-header">
        <div className="mp-golf-header__details">
          <p className="mp-eyebrow">{memberText("portalPresentation.member_available_times")}</p>
          <h4 className="mp-panel__title">{className}</h4>
          {classDetails ? <p className="mp-text-muted">{classDetails}</p> : null}
          {priceLabel ? <p className="mp-text-muted">{memberText("portalPresentation.member_member_price")} {priceLabel}</p> : null}
        </div>
        <div className="mp-golf-header__actions">
          <BackPillButton to={`/${clubCode}/fitness`}>{memberText("facilityBooking.backClasses")}</BackPillButton>
        </div>
      </div>

      <div className="mp-golf-scroll-area">
        <GolfDateCarousel
          bookingWindow={bookingWindow || DEFAULT_BOOKING_WINDOW_DAYS}
          timeZone={timeZone}
          selectedDateKey={selectedDateKey}
          onSelectDate={setSelectedDateKey}
          formatPrimary={(dateKey) => formatDayLabel(dateKey, timeZone, locale)}
          formatSecondary={(dateKey) => formatMonthShort(dateKey, timeZone, locale)}
        />

        <div className="mp-tee-slot-scroll">
          {fitnessClassError ? <div className="mp-warning" role="alert">{memberText(fitnessClassError)}</div> : null}
          {availabilityError ? <div className="mp-warning" role="alert">{memberText(availabilityError)}</div> : null}

          {fitnessClassLoading || availabilityLoading ? (
            <p className="mp-text-muted">{memberText("portalPresentation.member_loading_availability")}</p>
          ) : availability.length === 0 ? (
            <div className="mp-empty">{memberText("portalPresentation.member_no_available_times_for_this_day")}</div>
          ) : (
            <div className="mp-tee-slot-list">
              {availability.map((slot) => (
                <div key={slot.start_at || slot.start_time} className="mp-tee-slot">
                  <div>
                    <div className="mp-tee-slot__time">{slot.start_time}</div>
                    {slotMeta ? <div className="mp-tee-slot__meta">{slotMeta}</div> : null}
                  </div>
                  <div className="mp-tee-slot__actions">
                    <button
                      type="button"
                      className="mp-btn mp-btn--primary"
                      onClick={() => handleContinue(slot)}
                    > {memberText("portalPresentation.member_continue")} </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GolfCourseSplitLayout>
  )
}
